/**
 * WebSocket React Hooks
 *
 * React hooks for consuming WebSocket connections with automatic
 * state management and cleanup.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { WebSocketClient } from './client';
import type { ConnectionState, UseWebSocketOptions } from './types';

/**
 * Return type for the useWebSocket hook.
 */
export interface UseWebSocketReturn<T> {
    /** Current connection state */
    state: ConnectionState;
    /** Last received message (transformed if transform provided) */
    lastMessage: T | null;
    /** Last error event */
    error: Event | null;
    /** Send data to the server */
    send: (data: string | ArrayBuffer) => void;
    /** Manually trigger reconnect */
    reconnect: () => void;
    /** Manually disconnect */
    disconnect: () => void;
}

/**
 * React hook for WebSocket connections.
 *
 * Creates and manages a WebSocketClient instance, exposing connection state,
 * messages, and control methods as React state.
 *
 * @param url - WebSocket URL, or null to disable connection
 * @param options - Hook options including transform, callbacks, and reconnect config
 * @returns Object with state, lastMessage, error, send, reconnect, and disconnect
 *
 * @example
 * ```tsx
 * function PriceDisplay() {
 *   const { state, lastMessage, send } = useWebSocket<number>(
 *     'wss://api.example.com/prices',
 *     {
 *       transform: (event) => parseFloat(event.data),
 *       onMessage: (price) => console.log('Price:', price),
 *     }
 *   );
 *
 *   if (state === 'connecting') return <div>Connecting...</div>;
 *   if (state === 'disconnected') return <div>Disconnected</div>;
 *
 *   return <div>Price: {lastMessage}</div>;
 * }
 * ```
 *
 * @example Conditional connection
 * ```tsx
 * function AuthenticatedSocket({ token }: { token: string | null }) {
 *   // Only connect when token is available
 *   const url = token ? `wss://api.example.com/ws?token=${token}` : null;
 *   const { state, lastMessage } = useWebSocket(url);
 *   // ...
 * }
 * ```
 */
export function useWebSocket<T = MessageEvent>(
    url: string | null,
    options: UseWebSocketOptions<T> = {},
): UseWebSocketReturn<T> {
    // Extract config values (callbacks are accessed via ref to avoid dependency issues)
    const {
        autoConnect = true,
        reconnect = true,
        reconnectInterval,
        maxReconnectAttempts,
    } = options;

    // State
    const [state, setState] = useState<ConnectionState>('disconnected');
    const [lastMessage, setLastMessage] = useState<T | null>(null);
    const [error, setError] = useState<Event | null>(null);

    // Refs for stable callbacks and client instance
    const clientRef = useRef<WebSocketClient | null>(null);
    const optionsRef = useRef(options);

    // Keep options ref updated
    useEffect(() => {
        optionsRef.current = options;
    }, [options]);

    // Create/recreate client when URL changes
    useEffect(() => {
        // If no URL, ensure disconnected
        if (!url) {
            if (clientRef.current) {
                clientRef.current.disconnect();
                clientRef.current = null;
            }
            setState('disconnected');
            return;
        }

        // Create new client
        const client = new WebSocketClient({
            url,
            reconnect,
            reconnectInterval,
            maxReconnectAttempts,
        });

        clientRef.current = client;

        // Subscribe to state changes
        const unsubState = client.onStateChange((newState) => {
            setState(newState);
            optionsRef.current.onStateChange?.(newState);
        });

        // Subscribe to messages
        const unsubMessage = client.onMessage((event) => {
            const transformed = optionsRef.current.transform
                ? optionsRef.current.transform(event)
                : (event as unknown as T);

            setLastMessage(transformed);
            optionsRef.current.onMessage?.(transformed, event);
        });

        // Subscribe to errors
        const unsubError = client.onError((err) => {
            setError(err);
            optionsRef.current.onError?.(err);
        });

        // Subscribe to close events
        const unsubClose = client.onClose((event) => {
            optionsRef.current.onClose?.(event);
        });

        // Auto-connect if enabled
        if (autoConnect) {
            client.connect();
        }

        // Cleanup on unmount or URL change
        return () => {
            unsubState();
            unsubMessage();
            unsubError();
            unsubClose();
            client.disconnect();
            clientRef.current = null;
        };
    }, [url, autoConnect, reconnect, reconnectInterval, maxReconnectAttempts]);

    // Stable send function
    const send = useCallback((data: string | ArrayBuffer) => {
        if (clientRef.current) {
            clientRef.current.send(data);
        } else {
            throw new Error('WebSocket client not initialized');
        }
    }, []);

    // Stable reconnect function
    const reconnectFn = useCallback(() => {
        clientRef.current?.reconnect();
    }, []);

    // Stable disconnect function
    const disconnect = useCallback(() => {
        clientRef.current?.disconnect();
    }, []);

    return {
        state,
        lastMessage,
        error,
        send,
        reconnect: reconnectFn,
        disconnect,
    };
}
